<script setup>
import { computed } from "vue";

const props = defineProps({
  status: {
    type: String,
    default: null,
    validator: (value) => {
      return [null, "current", "former", "rehire_eligible", "rehire_ineligible"].includes(value);
    },
  },
  terminationReason: {
    type: String,
    default: null,
  },
  terminationDate: {
    type: String,
    default: null,
  },
  showRehireStatus: {
    type: Boolean,
    default: false,
  },
});

const isFormerStatus = computed(() => {
  return ["former", "rehire_eligible", "rehire_ineligible"].includes(props.status);
});

const showTooltip = computed(() => {
  return isFormerStatus.value && !!props.terminationReason;
});

const tooltipContent = computed(() => {
  if (props.terminationReason) {
    if (props.terminationDate) {
      return `${props.terminationReason} (${props.terminationDate})`;
    }
    return `${props.terminationReason}`;
  }
  return null;
});

const displayLabel = computed(() => {
  if (props.status === "current") return "Current employee";
  if (!props.showRehireStatus) return "Former employee";
  if (props.status === "former" || props.status === "rehire_eligible") return "Rehire eligible";
  if (props.status === "rehire_ineligible") return "Rehire ineligible";
  return null;
});

const statusClass = computed(() => {
  if (props.status === "current") return "current";
  if (!props.showRehireStatus) return "former";
  if (props.status === "former") return "rehire_eligible";
  return props.status;
});
</script>

<template>
  <el-tooltip
    :content="tooltipContent"
    :disabled="!showTooltip"
    :open-delay="250"
    placement="top"
  >
    <div class="employee-status" :class="statusClass">
      <span class="material-icons-round employee-status-icon">person</span>
      <span>{{ displayLabel }}</span>
    </div>
  </el-tooltip>
</template>

<style lang="scss" scoped>
.employee-status {
  height: 38px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 12px;
  background-color: white;
  border: 1px solid;

  .employee-status-icon {
    font-size: 18px;
  }

  &.current {
    background-color: #DFE7D8;
    border-color: #618A3D;
    color: #618A3D;
  }

  &.former {
    background-color: #E5E9F9;
    border-color: $blue-100;
    color: $blue-100;
  }

  &.rehire_eligible {
    background-color: #E5E9F9;
    border-color: $blue-100;
    color: $blue-100;
  }

  &.rehire_ineligible {
    background-color: #F9E5E5;
    border-color: #8B1A1A;
    color: #8B1A1A;
  }
}
</style>
